// /app/frontend/src/utils/debugSnapshot.js
/**
 * Debug Snapshot Utility
 * Captures Rule 0 debug data for rapid issue diagnosis.
 * Admin-only, no secrets, clipboard-ready.
 */

// Ring buffer for recent errors (max 20)
const MAX_ERRORS = 20;

/**
 * Install global error capture hooks.
 * Call once on app boot.
 */
export function installDebugErrorCapture() {
  if (typeof window === 'undefined') return;
  
  // Initialize error buffer
  window.__UL_DEBUG_ERRORS__ = window.__UL_DEBUG_ERRORS__ || [];
  
  // Capture uncaught errors
  const originalOnError = window.onerror;
  window.onerror = (message, source, lineno, colno, error) => {
    addError({
      type: 'error',
      message: String(message),
      source: source || '',
      line: lineno,
      col: colno,
      stack: error?.stack ? truncateStack(error.stack) : '',
    });
    if (originalOnError) {
      return originalOnError(message, source, lineno, colno, error);
    }
    return false;
  };
  
  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    addError({
      type: 'unhandledrejection',
      message: reason?.message || String(reason),
      stack: reason?.stack ? truncateStack(reason.stack) : '',
    });
  });
  
  console.log('[DebugSnapshot] Error capture installed');
}

/**
 * Add error to ring buffer
 */
function addError(errorInfo) {
  if (typeof window === 'undefined' || !window.__UL_DEBUG_ERRORS__) return;
  
  const entry = {
    ts: new Date().toISOString(),
    ...errorInfo,
  };
  
  window.__UL_DEBUG_ERRORS__.push(entry);
  
  // Keep only last MAX_ERRORS
  if (window.__UL_DEBUG_ERRORS__.length > MAX_ERRORS) {
    window.__UL_DEBUG_ERRORS__.shift();
  }
}

/**
 * Truncate stack traces to prevent huge snapshots
 */
function truncateStack(stack, maxLen = 2000) {
  if (!stack) return '';
  return stack.length > maxLen ? stack.slice(0, maxLen) + '...[truncated]' : stack;
}

/**
 * Get current theme from localStorage or DOM
 */
function getCurrentTheme() {
  if (typeof window === 'undefined') return 'unknown';
  
  // Check localStorage first
  const saved = localStorage.getItem('unitylove_theme');
  if (saved === 'dark' || saved === 'light') return saved;
  
  // Check DOM class
  if (document.documentElement.classList.contains('dark')) return 'dark';
  
  return 'light';
}

/**
 * Get environment type
 */
function getEnvType() {
  const url = window.location.hostname;
  if (url.includes('localhost') || url.includes('127.0.0.1')) return 'local';
  if (url.includes('preview')) return 'preview';
  return 'prod';
}

/**
 * Generate the complete debug snapshot.
 * 
 * @param {Object} options
 * @param {Object} options.user - Current user object {id, email, role}
 * @param {Object} options.entitlements - Entitlements/tier info
 * @param {string} options.buildTimestamp - Build timestamp from env
 * @param {string} options.buildCommit - Build commit from env
 * @returns {Object} Debug snapshot object
 */
export function getDebugSnapshot({ user, entitlements, buildTimestamp, buildCommit } = {}) {
  const now = new Date();
  
  return {
    // App info
    app: {
      buildTimestamp: buildTimestamp || process.env.REACT_APP_BUILD_TIMESTAMP || 'unknown',
      buildCommit: buildCommit || process.env.REACT_APP_BUILD_COMMIT || 'unknown',
      env: getEnvType(),
      backendUrl: window.location.origin || 'unknown',
    },
    
    // User info (sanitized - no tokens)
    user: {
      id: user?.id || 'not-logged-in',
      email: user?.email || 'unknown',
      role: user?.role || user?.is_admin ? 'admin' : 'user',
      tier: entitlements?.effective_tier || entitlements?.subscription?.tier || 'free',
      isVerified: entitlements?.verification?.identity_verified || user?.identity_verified || false,
    },
    
    // Current route
    route: {
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      fullUrl: window.location.href,
    },
    
    // Device info
    device: {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      cookiesEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
    },
    
    // Viewport
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
      devicePixelRatio: window.devicePixelRatio || 1,
      orientation: screen.orientation?.type || 'unknown',
    },
    
    // Theme
    theme: {
      mode: getCurrentTheme(),
      prefersDark: window.matchMedia?.('(prefers-color-scheme: dark)').matches || false,
    },
    
    // Network
    network: {
      online: navigator.onLine,
      effectiveType: navigator.connection?.effectiveType || 'unknown',
    },
    
    // Time
    time: {
      iso: now.toISOString(),
      local: now.toLocaleString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      timezoneOffset: now.getTimezoneOffset(),
    },
    
    // Recent errors (from ring buffer)
    errors: window.__UL_DEBUG_ERRORS__ || [],
    
    // Snapshot metadata
    _meta: {
      snapshotVersion: '1.0',
      generatedAt: now.toISOString(),
    },
  };
}

/**
 * Copy debug snapshot to clipboard
 * @returns {Promise<boolean>} Success status
 */
export async function copyDebugSnapshot(snapshotData) {
  const json = JSON.stringify(snapshotData, null, 2);
  
  try {
    await navigator.clipboard.writeText(json);
    return { success: true, json };
  } catch (err) {
    console.error('[DebugSnapshot] Clipboard write failed:', err);
    return { success: false, json, error: err.message };
  }
}

/**
 * Manually add an error to the buffer (for testing or explicit capture)
 */
export function captureError(message, details = {}) {
  addError({
    type: 'manual',
    message,
    ...details,
  });
}

/**
 * Get debug snapshot as JSON object - simplified version for non-admin use.
 * Used by ErrorBoundary to allow any user to copy debug info when crashes occur.
 * 
 * @param {Object} options
 * @param {boolean} options.includeErrors - Include captured errors (default: true)
 * @returns {Object} Debug snapshot object
 */
export function getDebugSnapshotJson({ includeErrors = true } = {}) {
  const now = new Date();
  
  const snapshot = {
    // App info
    app: {
      buildTimestamp: process.env.REACT_APP_BUILD_TIMESTAMP || 'unknown',
      buildCommit: process.env.REACT_APP_BUILD_COMMIT || 'unknown',
      env: getEnvType(),
    },
    
    // Current route
    route: {
      pathname: window.location.pathname,
      search: window.location.search,
    },
    
    // Device info (sanitized)
    device: {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
      onLine: navigator.onLine,
    },
    
    // Viewport
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    
    // Theme
    theme: getCurrentTheme(),
    
    // Time
    time: {
      iso: now.toISOString(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
    
    // Snapshot metadata
    _snapshotVersion: '1.1',
  };
  
  // Include errors if requested
  if (includeErrors && window.__UL_DEBUG_ERRORS__?.length > 0) {
    snapshot.errors = window.__UL_DEBUG_ERRORS__;
  }
  
  return snapshot;
}
